import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import AddManualQuoteOption from '@/components/modals/quotes/AddManualQuoteOption';
import ConfirmBookingDialog from '@/components/modals/bookings/ConfirmBookingDialog';
import {
  MapPin,
  Building2,
  CheckCircle,
  AlertCircle,
  Shield,
  Loader2,
  Plus,
  Monitor,
  Laptop
} from 'lucide-react';

/**
 * QuoteOptionsContent - Displays and manages quote options for live quotes
 */
const QuoteOptionsContent = ({ booking, onRefresh }) => {
  const [quoteOptions, setQuoteOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingQuote, setIsAddingQuote] = useState(false);
  const [selectedQuoteOption, setSelectedQuoteOption] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);

  useEffect(() => {
    if (booking?.id) {
      fetchQuoteOptions();
    }
  }, [booking?.id]);

  const fetchQuoteOptions = async () => {
    if (!booking?.id) return;

    try {
      setLoading(true);

      const { data: options, error: optionsError } = await supabase
        .from('quote_options')
        .select('*')
        .eq('training_quote_id', booking.id)
        .order('quote_value', { ascending: true });

      if (optionsError) throw optionsError;

      // Get unique trainer IDs
      const trainerIds = [...new Set(
        options
          ?.filter(opt => opt.training_provider_id)
          ?.map(opt => opt.training_provider_id)
      )];

      // Fetch trainer details
      let trainersMap = {};
      if (trainerIds.length > 0) {
        const { data: trainers, error: trainersError } = await supabase
          .from('trainers')
          .select('id, trainer_name, booking_email, company_id')
          .in('id', trainerIds);

        if (!trainersError && trainers) {
          trainersMap = trainers.reduce((acc, trainer) => {
            acc[trainer.id] = trainer;
            return acc;
          }, {});
        }
      }

      // Attach trainer info to quote options
      const optionsWithTrainers = options?.map(option => ({
        ...option,
        trainers: option.training_provider_id ? trainersMap[option.training_provider_id] : null
      })) || [];

      setQuoteOptions(optionsWithTrainers);
    } catch (error) {
      console.error('Error fetching quote options:', error);
      toast.error('Failed to load quote options');
    } finally {
      setLoading(false);
    }
  };

  // Group quote options by training provider
  const groupedQuoteOptions = quoteOptions.reduce((acc, option) => {
    const providerKey = option.training_provider_id || 'bohsa';
    if (!acc[providerKey]) {
      acc[providerKey] = {
        provider: option.trainers || { trainer_name: 'BOHSA', company_id: null },
        options: []
      };
    }
    acc[providerKey].options.push(option);
    return acc;
  }, {});

  const formatPricingModel = (model) => {
    if (!model) return null;
    const labels = {
      per_person: 'Per Person',
      per_course: 'Per Course',
      per_day: 'Per Day',
      fixed: 'Fixed Price'
    };
    return labels[model] || model;
  };

  const handleBookClick = (quoteOption) => {
    setSelectedQuoteOption(quoteOption);
    setShowBookingDialog(true);
  };

  const handleBookingSuccess = () => {
    if (onRefresh) onRefresh();
  };

  const handleCloseBookingDialog = () => {
    setShowBookingDialog(false);
    setSelectedQuoteOption(null);
  };

  return (
    <div className="space-y-6">
      {/* Header with Add button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Quote Options</h3>
          <p className="text-sm text-gray-500">
            {quoteOptions.length} option{quoteOptions.length !== 1 ? 's' : ''} from {Object.keys(groupedQuoteOptions).length} provider{Object.keys(groupedQuoteOptions).length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={() => setIsAddingQuote(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Quote
        </Button>
      </div>

      {/* Quote Options Table */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : quoteOptions.length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedQuoteOptions)
            .sort(([keyA, { provider: providerA }], [keyB, { provider: providerB }]) => {
              // BOHSA (no company_id) first
              if (!providerA.company_id && providerB.company_id) return -1;
              if (providerA.company_id && !providerB.company_id) return 1;
              return providerA.trainer_name.localeCompare(providerB.trainer_name);
            })
            .map(([providerKey, { provider, options }]) => (
              <div key={providerKey}>
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-3">
                  {provider.company_id ? (
                    <Building2 className="h-4 w-4" />
                  ) : (
                    <Shield className="h-4 w-4" />
                  )}
                  {provider.trainer_name}
                  {!provider.company_id && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 ml-2">
                      Verified
                    </Badge>
                  )}
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs">Course Name</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Delivery</TableHead>
                        <TableHead className="text-xs">Location</TableHead>
                        <TableHead className="text-xs">Duration</TableHead>
                        <TableHead className="text-xs">Amount</TableHead>
                        <TableHead className="text-xs text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {options.map((option) => (
                        <TableRow key={option.id} className="hover:bg-gray-50/50">
                          <TableCell>
                            <p className="font-medium text-sm">{option.course_name || 'Training Course'}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">
                              {option.dates && option.dates[0]?.startDate ?
                                new Date(option.dates[0].startDate).toLocaleDateString('en-GB', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                }) :
                                'Flexible'}
                            </p>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              {option.course_delivery === 'online' ? (
                                <Monitor className="h-3.5 w-3.5 text-gray-400" />
                              ) : option.course_delivery === 'hybrid' ? (
                                <Laptop className="h-3.5 w-3.5 text-gray-400" />
                              ) : (
                                <MapPin className="h-3.5 w-3.5 text-gray-400" />
                              )}
                              <p className="text-sm capitalize">{option.course_delivery || 'Onsite'}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{option.onsite_postcode || '-'}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{option.course_duration || '-'}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-semibold text-blue-600">
                              £{option.quote_value?.toLocaleString() || '0'}
                            </p>
                            {option.pricing_model && (
                              <p className="text-xs text-muted-foreground">{formatPricingModel(option.pricing_model)}</p>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {option.status === 256 ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <CheckCircle className="h-3.5 w-3.5 mr-1" />
                                Booked
                              </Badge>
                            ) : (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleBookClick(option)}
                              >
                                Book
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h4 className="text-sm font-medium text-gray-900 mb-1">No quote options yet</h4>
          <p className="text-sm text-gray-500 mb-4">
            Quote options will appear here when providers respond
          </p>
          <Button variant="outline" onClick={() => setIsAddingQuote(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Quote Manually
          </Button>
        </div>
      )}

      {/* Add Quote Modal */}
      <AddManualQuoteOption
        open={isAddingQuote}
        onClose={() => setIsAddingQuote(false)}
        bookingId={booking?.id}
        courseName={booking?.course_name}
        onSuccess={fetchQuoteOptions}
      />

      {/* Confirm Booking Dialog */}
      <ConfirmBookingDialog
        open={showBookingDialog}
        onClose={handleCloseBookingDialog}
        booking={booking}
        quoteOption={selectedQuoteOption}
        onSuccess={handleBookingSuccess}
      />
    </div>
  );
};

export default QuoteOptionsContent;
